import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Search, Upload, Image as ImageIcon, Link as LinkIcon } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';

const SearchBar = ({ onSearch, isLoading, initialClaim = '', autoFocus = false }) => {
  const [searchText, setSearchText] = useState(initialClaim);
  const [searchUrl, setSearchUrl] = useState('');
  const [activeTab, setActiveTab] = useState('text');
  const [isDragging, setIsDragging] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);
  const dropZoneRef = useRef(null);

  // URL pattern to detect links
  const urlPattern = /^(https?:\/\/)?(www\.)?(youtube\.com|youtu\.be|tiktok\.com|instagram\.com|twitter\.com|x\.com|facebook\.com|fb\.watch|vimeo\.com|dailymotion\.com|twitch\.tv|reddit\.com|soundcloud\.com)[\/\S]*$/i;
  
  // General URL pattern for any link
  const generalUrlPattern = /^(https?:\/\/)[^\s]+$/i;

  // Auto-detect pasted links and switch tabs
  const handleTextChange = (e) => {
    const value = e.target.value;
    setSearchText(value);
    
    // Check if pasted content looks like a video URL
    if (urlPattern.test(value.trim())) {
      setSearchUrl(value.trim());
      setActiveTab('url');
      setSearchText(''); // Clear text field
    }
  };

  // Handle paste event for text input to detect URLs
  const handleTextPaste = (e) => {
    const pastedText = e.clipboardData?.getData('text');
    if (pastedText && urlPattern.test(pastedText.trim())) {
      e.preventDefault();
      setSearchUrl(pastedText.trim());
      setActiveTab('url');
    }
  };

  // Check for prefilled claim from browser extension or initialClaim prop
  useEffect(() => {
    // First check sessionStorage for extension prefill
    const prefillClaim = sessionStorage.getItem('prefill_claim');
    if (prefillClaim) {
      setSearchText(prefillClaim);
      sessionStorage.removeItem('prefill_claim'); // Clear after use
    } else if (initialClaim) {
      setSearchText(initialClaim);
    }
  }, [initialClaim]);

  const handleTextSearch = (e) => {
    e.preventDefault();
    if (searchText.trim()) {
      onSearch({ type: 'text', content: searchText });
    }
  };

  const handleUrlSearch = (e) => {
    e.preventDefault();
    if (searchUrl.trim()) {
      onSearch({ type: 'url', content: searchUrl });
    }
  };

  // URL FEATURE MOTHBALLED - Legal risk and reliability issues
  // Keeping code for future use when URL scraping is more robust
  // const [searchUrl, setSearchUrl] = useState('');
  // const handleUrlSearch = (e) => {
  //   e.preventDefault();
  //   if (searchUrl.trim()) {
  //     onSearch({ type: 'url', content: searchUrl });
  //   }
  // };

  // Client-side upload safeguards. Frontend takes a best-effort compression
  // pass (JPEG/PNG/WebP), but HEIC and other formats browsers can't decode
  // are handed to the backend untouched — the server normalizer (pillow +
  // pillow_heif) will resize and re-encode them safely.
  const MAX_UPLOAD_MB = 40; // pathological cap — anything above will be
                            // rejected by ingress anyway
  const CLIENT_MAX_DIM = 1600; // longest side after client-side compression

  // Compress and resize image before upload (JPEG/PNG/WebP only).
  // Caps BOTH width and height so portrait phone photos aren't left oversized.
  const compressImage = (file, maxDim = CLIENT_MAX_DIM, quality = 0.75) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          let width = img.width;
          let height = img.height;

          // Scale down while preserving aspect on the LONGEST side.
          const longest = Math.max(width, height);
          if (longest > maxDim) {
            const scale = maxDim / longest;
            width = Math.round(width * scale);
            height = Math.round(height * scale);
          }

          canvas.width = width;
          canvas.height = height;

          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);

          // Convert to JPEG for smaller size
          const compressedDataUrl = canvas.toDataURL('image/jpeg', quality);
          resolve(compressedDataUrl);
        };
        img.onerror = () => reject(new Error('BROWSER_DECODE_FAILED'));
        img.src = e.target.result;
      };
      reader.onerror = () => reject(new Error('FILE_READ_FAILED'));
      reader.readAsDataURL(file);
    });
  };

  // Read a file as a data URL without any transformation. Used as the
  // "hand off to backend" path for HEIC/HEIF/etc.
  const readFileAsDataURL = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => resolve(e.target.result);
      reader.onerror = () => reject(new Error('FILE_READ_FAILED'));
      reader.readAsDataURL(file);
    });

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (file) {
      await processImageFile(file);
    }
  };

  // Process image file (used by upload, drag, and paste)
  // Flow:
  //   1. Bail out if file is > MAX_UPLOAD_MB (ingress will reject anyway).
  //   2. Try client-side JPEG compression — succeeds for JPEG / PNG / WebP.
  //   3. If browser can't decode (typically iPhone HEIC/HEIF), hand the raw
  //      bytes to the backend. The server-side normalizer (pillow_heif)
  //      will decode, auto-orient, resize, and re-encode as JPEG.
  const processImageFile = async (file) => {
    if (!file.type.startsWith('image/')) {
      alert('Please upload an image file.');
      return;
    }

    const sizeMB = file.size / (1024 * 1024);
    if (sizeMB > MAX_UPLOAD_MB) {
      alert(
        `Image is too large (${sizeMB.toFixed(1)} MB). ` +
        `Please share a photo under ${MAX_UPLOAD_MB} MB — try taking the shot again ` +
        `or sharing it through a chat app first (that usually compresses it).`
      );
      return;
    }

    try {
      // Try client-side compression first (JPEG/PNG/WebP)
      const compressedImage = await compressImage(file, CLIENT_MAX_DIM, 0.75);
      setPreviewImage(compressedImage);
      setActiveTab('image');
      onSearch({ type: 'image', content: compressedImage, fileName: file.name });
    } catch (err) {
      // Browser couldn't decode (e.g. HEIC / HEIF from iPhone) — hand raw bytes
      // to the backend. The server normalizer will handle it end-to-end.
      try {
        const rawDataUrl = await readFileAsDataURL(file);
        setPreviewImage(rawDataUrl);
        setActiveTab('image');
        onSearch({ type: 'image', content: rawDataUrl, fileName: file.name });
      } catch (readErr) {
        console.error('Failed to read image file:', readErr);
        alert('Could not read that image. Please try a different photo.');
      }
    }
  };

  // Handle drag events
  const handleDragEnter = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback(async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    
    const files = e.dataTransfer?.files;
    if (files && files.length > 0) {
      await processImageFile(files[0]);
    }
  }, []);

  // Handle paste event (Ctrl+V)
  useEffect(() => {
    const handlePaste = async (e) => {
      const items = e.clipboardData?.items;
      if (!items) return;
      
      for (const item of items) {
        if (item.type.startsWith('image/')) {
          e.preventDefault();
          const file = item.getAsFile();
          if (file) {
            await processImageFile(file);
          }
          break;
        }
      }
    };
    
    document.addEventListener('paste', handlePaste);
    return () => document.removeEventListener('paste', handlePaste);
  }, []);

  return (
    <div className="w-full max-w-2xl mx-auto">
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-3 mb-4 bg-slate-200 dark:bg-slate-700 p-1.5 rounded-full gap-1">
          <TabsTrigger value="text" className="rounded-full bg-blue-100 dark:bg-slate-600 text-slate-700 dark:text-slate-200 data-[state=active]:bg-blue-600 data-[state=active]:text-white data-[state=active]:shadow-md transition-all text-xs sm:text-sm font-medium">
            <Search className="w-4 h-4 mr-1 sm:mr-2" />
            <span className="hidden sm:inline">Check </span>Post
          </TabsTrigger>
          <TabsTrigger value="url" className="rounded-full bg-blue-100 dark:bg-slate-600 text-slate-700 dark:text-slate-200 data-[state=active]:bg-blue-600 data-[state=active]:text-white data-[state=active]:shadow-md transition-all text-xs sm:text-sm font-medium">
            <LinkIcon className="w-4 h-4 mr-1 sm:mr-2" />
            <span className="hidden sm:inline">Check </span>Video
          </TabsTrigger>
          <TabsTrigger value="image" className="rounded-full bg-blue-100 dark:bg-slate-600 text-slate-700 dark:text-slate-200 data-[state=active]:bg-blue-600 data-[state=active]:text-white data-[state=active]:shadow-md transition-all text-xs sm:text-sm font-medium">
            <ImageIcon className="w-4 h-4 mr-1 sm:mr-2" />
            <span className="hidden sm:inline">Check </span>Image
          </TabsTrigger>
        </TabsList>

        <TabsContent value="text" className="mt-0">
          <form onSubmit={handleTextSearch} className="relative">
            <div className="flex items-center gap-3 p-2 sm:p-3 bg-white rounded-full shadow-xl border-2 border-slate-300 hover:border-blue-500 focus-within:border-blue-500 focus-within:ring-4 focus-within:ring-blue-100 transition-all duration-300">
              <Search className="w-5 h-5 text-blue-600 ml-3" />
              <Input
                type="text"
                placeholder="Fact or fake? Paste it here..."
                value={searchText}
                onChange={handleTextChange}
                onPaste={handleTextPaste}
                className="flex-1 border-0 focus-visible:ring-0 focus-visible:ring-offset-0 text-base sm:text-lg text-slate-800 placeholder:text-slate-400 bg-transparent"
                disabled={isLoading}
                autoFocus={autoFocus}
                data-testid="search-text-input"
              />
              <Button
                type="submit"
                disabled={isLoading || !searchText.trim()}
                className="bg-blue-600 hover:bg-blue-700 text-white rounded-full px-6 sm:px-8 py-2.5 font-semibold shadow-lg hover:shadow-xl transition-all duration-300"
                data-testid="search-text-btn"
              >
                {isLoading ? 'Checking...' : 'Check'}
              </Button>
            </div>
          </form>
          <p className="text-center text-sm text-slate-500 mt-3">
            💡 Paste any social media post, news headline, or viral message
          </p>
        </TabsContent>

        <TabsContent value="url" className="mt-0">
          <form onSubmit={handleUrlSearch} className="relative">
            <div className="flex items-center gap-3 p-2 sm:p-3 bg-white rounded-full shadow-xl border-2 border-slate-300 hover:border-blue-500 focus-within:border-blue-500 focus-within:ring-4 focus-within:ring-blue-100 transition-all duration-300">
              <LinkIcon className="w-5 h-5 text-blue-600 ml-3" />
              <Input
                type="text"
                placeholder="Paste video URL (YouTube, TikTok, etc.)..."
                value={searchUrl}
                onChange={(e) => setSearchUrl(e.target.value)}
                className="flex-1 border-0 focus-visible:ring-0 focus-visible:ring-offset-0 text-base sm:text-lg text-slate-800 placeholder:text-slate-400 bg-transparent"
                disabled={isLoading}
                data-testid="search-url-input"
              />
              <Button
                type="submit"
                disabled={isLoading || !searchUrl.trim()}
                className="bg-blue-600 hover:bg-blue-700 text-white rounded-full px-6 sm:px-8 py-2.5 font-semibold shadow-lg hover:shadow-xl transition-all duration-300"
                data-testid="search-url-btn"
              >
                {isLoading ? 'Analyzing...' : 'Check'}
              </Button>
            </div>
          </form>
          <p className="text-center text-sm text-slate-500 mt-3">
            🎥 Supports YouTube, TikTok, Instagram, Twitter/X, Facebook<br/>
            <span className="text-amber-600 dark:text-amber-400 font-medium">⏱️ Videos must be under 10 minutes</span>
          </p>
        </TabsContent>

        {/* URL TAB MOTHBALLED - Keeping for future use
        <TabsContent value="url" className="mt-0">
          <form onSubmit={handleUrlSearch} className="relative">
            <div className="flex items-center gap-3 p-3 bg-white rounded-full shadow-lg border-2 border-slate-200 hover:border-blue-400 transition-all duration-300">
              <LinkIcon className="w-5 h-5 text-slate-400 ml-3" />
              <Input
                type="text"
                placeholder="Paste social media URL to verify..."
                value={searchUrl}
                onChange={(e) => setSearchUrl(e.target.value)}
                className="flex-1 border-0 focus-visible:ring-0 focus-visible:ring-offset-0 text-base"
                disabled={isLoading}
              />
              <Button
                type="submit"
                disabled={isLoading || !searchUrl.trim()}
                className="bg-blue-600 hover:bg-blue-700 text-white rounded-full px-8 transition-all duration-300"
              >
                {isLoading ? 'Checking...' : 'Verify'}
              </Button>
            </div>
          </form>
        </TabsContent>
        */}

        <TabsContent value="image" className="mt-0">
          <div 
            ref={dropZoneRef}
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
            className={`flex items-center gap-3 p-6 sm:p-8 bg-white rounded-3xl shadow-xl border-2 border-dashed transition-all duration-300 ${
              isDragging 
                ? 'border-blue-500 bg-blue-50 scale-[1.02]' 
                : 'border-slate-300 hover:border-blue-500'
            }`}
          >
            <div className="w-full text-center">
              {previewImage ? (
                <div className="mb-4">
                  <img 
                    src={previewImage} 
                    alt="Preview" 
                    className="max-h-32 mx-auto rounded-lg shadow-md"
                  />
                  <button 
                    onClick={() => setPreviewImage(null)}
                    className="text-sm text-slate-500 hover:text-red-500 mt-2"
                  >
                    Clear image
                  </button>
                </div>
              ) : (
                <>
                  <ImageIcon className="w-10 h-10 text-blue-600 mx-auto mb-3" />
                  <label htmlFor="image-upload" className="cursor-pointer">
                    <span className="text-slate-700 text-base font-medium block mb-1">
                      {isDragging ? '📷 Drop image here!' : 'Drop, paste, or upload an image'}
                    </span>
                    <span className="text-sm text-slate-500">Memes, tweets, posts, headlines • Ctrl+V to paste</span>
                  </label>
                </>
              )}
              <Input
                id="image-upload"
                type="file"
                accept="image/jpeg,image/png,image/webp,image/gif,image/heic,image/heif,.heic,.heif"
                onChange={handleImageUpload}
                className="hidden"
                disabled={isLoading}
              />
              <Button
                onClick={() => document.getElementById('image-upload').click()}
                disabled={isLoading}
                className="bg-blue-600 hover:bg-blue-700 text-white rounded-full px-8 mt-4 font-semibold shadow-lg hover:shadow-xl transition-all duration-300"
              >
                {isLoading ? 'Processing...' : previewImage ? 'Upload Different Image' : 'Upload Image'}
              </Button>
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default SearchBar;